import React, { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { Empty } from "antd";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";

type GeoHotspot = {
  label: string;
  value: number;
  lat?: number | null;
  lng?: number | null;
};

type MappedHotspot = GeoHotspot & { lat: number; lng: number };

type Props = {
  data: GeoHotspot[];
};

const hasCoords = (d: GeoHotspot): d is MappedHotspot =>
  Number.isFinite(d.lat as number) && Number.isFinite(d.lng as number);

const formatValue = (v: number) =>
  Number.isInteger(v) ? String(v) : v.toFixed(1);

// Proportional-symbol ("bubble") marker: radius scales with the metric value.
const makeBubbleIcon = (value: number, maxValue: number) => {
  const minR = 13;
  const maxR = 34;
  const ratio = maxValue > 0 ? value / maxValue : 0;
  const r = Math.round(minR + ratio * (maxR - minR));
  const size = r * 2;
  return L.divIcon({
    className: "",
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:rgba(160,114,235,0.85);
      border:2px solid #fff;
      box-shadow:0 2px 8px rgba(74,28,150,0.35);
      display:flex;align-items:center;justify-content:center;
      color:#fff;font-weight:700;
      font-size:${Math.max(10, Math.round(r * 0.6))}px;
      font-family:Poppins, sans-serif;
      line-height:1;
    ">${formatValue(value)}</div>`,
    iconSize: [size, size],
    iconAnchor: [r, r],
    popupAnchor: [0, -r],
  });
};

// Purple cluster bubble showing how many locations are grouped at this zoom.
const createClusterIcon = (cluster: L.MarkerCluster) => {
  const count = cluster.getChildCount();
  const size = count < 10 ? 36 : count < 50 ? 46 : 56;
  return L.divIcon({
    className: "",
    html: `<div style="
      width:${size}px;height:${size}px;border-radius:50%;
      background:rgba(74,28,150,0.9);
      border:3px solid #fff;
      box-shadow:0 2px 10px rgba(74,28,150,0.45);
      display:flex;align-items:center;justify-content:center;
      color:#fff;font-weight:700;font-size:14px;
      font-family:Poppins, sans-serif;
    ">${count}</div>`,
    iconSize: L.point(size, size, true),
  });
};

const GeoHotspotsCard: React.FC<Props> = ({ data }) => {
  const { t } = useTranslation("dashboardModals");
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    const handleBeforePrint = () => mapRef.current?.invalidateSize();
    window.addEventListener("beforeprint", handleBeforePrint);
    return () => window.removeEventListener("beforeprint", handleBeforePrint);
  }, []);

  if (!data || !data.length) {
    return (
      <Empty
        description={t("geoHotspotsCard.noData")}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  const mapItems = data.filter(hasCoords);

  // The PDF export can't reliably capture the live Leaflet tiles (OpenStreetMap
  // serves them without CORS headers, which taints the canvas html2canvas needs
  // to read), so it always renders this bar-list snapshot instead of the map —
  // hidden on screen, shown only while the export is capturing this card.
  const renderBarList = (items: GeoHotspot[]) => {
    const barItems = items.slice(0, 10);
    const maxValue = Math.max(...barItems.map((item) => item.value), 0);

    return (
      <div
        style={{
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {barItems.map((item) => (
          <div key={item.label}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
                fontSize: 13,
              }}
            >
              <span>{item.label}</span>
              <strong>{formatValue(item.value)}</strong>
            </div>

            <div
              style={{
                width: "100%",
                height: 10,
                background: "#f0f0f0",
                borderRadius: 999,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${maxValue ? (item.value / maxValue) * 100 : 0}%`,
                  height: "100%",
                  background: "linear-gradient(90deg, #8c6cff, #4A1C96)",
                }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (mapItems.length > 0) {
    const avgLat =
      mapItems.reduce((s, d) => s + d.lat, 0) / mapItems.length;
    const avgLng =
      mapItems.reduce((s, d) => s + d.lng, 0) / mapItems.length;
    const maxValue = Math.max(...mapItems.map((d) => d.value), 0);
    const unmapped = data.length - mapItems.length;

    // Draw larger bubbles first so smaller ones stay clickable on top.
    const ordered = [...mapItems].sort((a, b) => b.value - a.value);

    return (
      <div>
        <div className="card-live-view">
          <div style={{ height: 320, borderRadius: 8, overflow: "hidden" }}>
            <MapContainer
              ref={mapRef}
              center={[avgLat, avgLng]}
              zoom={4}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MarkerClusterGroup
                chunkedLoading
                showCoverageOnHover={false}
                maxClusterRadius={55}
                iconCreateFunction={createClusterIcon}
              >
                {ordered.map((d, i) => (
                  <Marker
                    key={i}
                    position={[d.lat, d.lng]}
                    icon={makeBubbleIcon(d.value, maxValue)}
                  >
                    <Popup>
                      <div
                        style={{
                          fontFamily: "Poppins, sans-serif",
                          fontSize: 13,
                          minWidth: 120,
                        }}
                      >
                        <div style={{ fontWeight: 700, marginBottom: 2 }}>
                          {d.label}
                        </div>
                        <div>
                          {t("geoHotspotsCard.value")}:{" "}
                          <strong>{formatValue(d.value)}</strong>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            </MapContainer>
          </div>
          {unmapped > 0 && (
            <div style={{ marginTop: 6, fontSize: 12, color: "#999" }}>
              {t("geoHotspotsCard.unmappedLocations", { count: unmapped })}
            </div>
          )}
        </div>
        <div className="card-pdf-snapshot" style={{ display: "none" }}>
          {renderBarList(data)}
        </div>
      </div>
    );
  }

  // Fallback: ranked bar list when the backend returned no coordinates
  // (geocoding disabled / unresolvable locations).
  return renderBarList(data);
};

export default GeoHotspotsCard;
